import * as React from 'react';

import DiagnosticCard, { DiagnosticCardData } from './diagnosticCard';
import FilterRow from './filterRow';
import {
  CLOSED,
  OPEN,
  GROUP_BY_NONE,
  GROUP_BY_OPTIONS,
  GROUP_BY_PACK_TYPE,
  SORT_NEWEST_ASSIGNED,
  createFilters,
} from './filters';
import PackCard from './packCard';
import { NoResultsState, Section, TruncatedCardGrid, createPackCard, sectionBoltIcon, sectionIcon } from './pagePrimitives';
import { buildAllDiagnosticCards, createCardResolver, createPackResolver } from './resolution';
import {
  ALL,
  BOOSTED_SECTION_ID,
  Pack,
  StudentCompletion,
  diagnosticCardId,
  isProperDiagnosticUnit,
  packCardId,
  sortByStatusThenLastName,
  WELCOME_SNACKBAR_STORAGE_KEY,
} from './shared';
import { CardPart, useShownCardParts } from './useShownCardParts';
import { useBoostedPacks, usePackData } from './usePackData';
import { useScrollRestoredAfterLoad } from './useScrollRestoredAfterLoad';
import { useTopCardHeldInPlace } from './useTopCardHeldInPlace';

import useSnackbarMonitor from '../../../../Shared/hooks/useSnackbarMonitor';
import {
  DropdownInput,
  EXTRA_SMALL_ICON_BASE_SRC,
  Snackbar,
  Spinner,
  defaultSnackbarTimeout,
  getPageTitle,
} from '../../../../Shared/index';

function partition<T>(list: T[], predicate: (item: T) => boolean): [T[], T[]] {
  const matches: T[] = []
  const rest: T[] = []
  list.forEach(item => (predicate(item) ? matches : rest).push(item))
  return [matches, rest]
}

const GRID_VIEW = 'grid'
const LIST_VIEW = 'list'
const DENSITY_STORAGE_KEY = 'manageActivitiesV2Density'
const GROUP_BY_STORAGE_KEY = 'manageActivitiesV2GroupBy'
const SCROLL_POSITION_KEY = 'manageActivitiesV2ScrollPosition'

export default function ManageActivitiesV2(_props: any) {
  const [view, setView] = React.useState(new URLSearchParams(window.location.search).get('view') === 'closed' ? CLOSED : OPEN)
  const [density, setDensity] = React.useState(window.localStorage.getItem(DENSITY_STORAGE_KEY) === GRID_VIEW ? GRID_VIEW : LIST_VIEW)
  const [shownParts, setShownParts] = useShownCardParts(density === GRID_VIEW)
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [snackbarCopy, setSnackbarCopy] = React.useState('')
  const [sortBy, setSortBy] = React.useState<string>(SORT_NEWEST_ASSIGNED)
  const [selectedCompletion, setSelectedCompletion] = React.useState<string>(ALL)
  const [groupBy, setGroupBy] = React.useState(() => {
    const stored = window.localStorage.getItem(GROUP_BY_STORAGE_KEY)
    return GROUP_BY_OPTIONS.some(option => option.value === stored) ? stored : GROUP_BY_PACK_TYPE
  })
  const [searchQuery, setSearchQuery] = React.useState('')
  const cardViewProps = { activitySearch: searchQuery, isCompact: density === GRID_VIEW, shownParts }
  const rememberTopCard = useTopCardHeldInPlace(`${density}-${groupBy}-${shownParts.join()}`)
  const [revealCardKey, setRevealCardKey] = React.useState<string | null>(null)

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  // sections only read it as they mount after a Group by switch; cleared so later visits start collapsed
  React.useEffect(() => { if (revealCardKey) { setRevealCardKey(null) } }, [revealCardKey])

  function reportLoadError() {
    setSnackbarCopy('Something went wrong loading your activity packs. Refresh to try again.')
    setShowSnackbar(true)
  }

  const {
    classrooms,
    diagnosticClassrooms,
    handleClassroomChange,
    invalidatePendingFetches,
    loaded,
    packs,
    recommendationCounts,
    refetchPackData,
    selectedClassroomId,
    setUnitProgress,
    unitProgress,
  } = usePackData({ onLoadError: reportLoadError })

  function showBoostSnackbar(message: string) {
    setSnackbarCopy(message)
    setShowSnackbar(true)
  }

  const { boostToggler, isUnitBoosted } = useBoostedPacks({ unitProgress, setUnitProgress, selectedClassroomId, refetchPackData, invalidatePendingFetches, onBoostToggled: showBoostSnackbar })

  React.useEffect(() => {
    document.title = getPageTitle('Manage Activities V2', view === CLOSED ? 'Closed Activity Packs' : 'Open Activity Packs')
  }, [view])

  React.useEffect(() => {
    if (!window.sessionStorage.getItem(WELCOME_SNACKBAR_STORAGE_KEY)) { return }

    window.sessionStorage.removeItem(WELCOME_SNACKBAR_STORAGE_KEY)
    setSnackbarCopy('Welcome to the new experience. Change back any time in My Account.')
    setShowSnackbar(true)
  }, [])

  function handlePackChanged(newSnackbarCopy?: string) {
    refetchPackData()
    if (newSnackbarCopy) {
      setSnackbarCopy(newSnackbarCopy)
      setShowSnackbar(true)
    }
  }

  const packResolver = React.useMemo(() => createPackResolver({ packs, selectedClassroomId }), [packs, selectedClassroomId])
  const allDiagnosticCards = React.useMemo(() => buildAllDiagnosticCards(diagnosticClassrooms, packResolver), [diagnosticClassrooms, packResolver])
  const cardResolver = React.useMemo(() => createCardResolver({ packResolver, allDiagnosticCards, unitProgress, selectedClassroomId }), [packResolver, allDiagnosticCards, unitProgress, selectedClassroomId])
  const { backingPackFor, diagnosticCardIsOpen, postBackingPackFor } = packResolver
  const { diagnosticBlobKey, diagnosticCardForPack, diagnosticRendersAsCustomPack, diagnosticStudentsFor, packIsCardAnchor, packRendersJourneyCard, parentDiagnosticHref, parentDiagnosticLabel } = cardResolver
  const { compareBySort, diagnosticCardMatchesFilters, diagnosticTime, packMatchesFilters, packSortTime, sortByTime, sortPacks } = createFilters({ view, selectedClassroomId, selectedCompletion, searchQuery, sortBy, unitProgress, packResolver })

  const filteredPacks = packs.filter(packMatchesFilters)

  const diagnosticCards: DiagnosticCardData[] = allDiagnosticCards.filter(diagnosticCardMatchesFilters)

  const recommendationPacks = filteredPacks.filter(pack => unitProgress[pack.unitId]?.from_recommendations)
  const premadeAndCustomPacks = filteredPacks.filter(pack => {
    const progress = unitProgress[pack.unitId]
    if (progress?.from_recommendations) { return false }
    if (!isProperDiagnosticUnit(pack)) { return true }
    const card = diagnosticCardForPack(pack)
    if (card && !packIsCardAnchor(pack, card)) { return true }
    return diagnosticRendersAsCustomPack(pack)
  })

  const classroomOptions = classrooms.map(c => ({ label: c.name, value: c.id }))

  const openDiagnosticCards = diagnosticCards.filter(diagnosticCardIsOpen)
  const closedDiagnosticCards = diagnosticCards.filter(card => !diagnosticCardIsOpen(card))
  const viewDiagnosticCards = view === OPEN ? openDiagnosticCards : closedDiagnosticCards
  const sortedDiagnosticCards = viewDiagnosticCards
    .map(card => [diagnosticTime(card), card] as [number, DiagnosticCardData])
    .sort(([aTime], [bTime]) => compareBySort(aTime, bTime))
    .map(([, card]) => card)

  function diagnosticCardStudents(record: { activity_id?: number, students?: StudentCompletion[] } | undefined, classroomId: number): StudentCompletion[] {
    if (record?.students) { return sortByStatusThenLastName(record.students) }
    return record?.activity_id ? diagnosticStudentsFor(record.activity_id, classroomId) : []
  }

  function renderDiagnosticCard(card: DiagnosticCardData) {
    const backingPack = backingPackFor(card)
    const postBackingPack = postBackingPackFor(card)
    const post = card.post as { activity_id?: number, students?: Array<{ id: number, name: string | null, completed: boolean }> } | undefined
    const preStudents = diagnosticCardStudents(card.pre, card.classroomId)
    const postStudents = diagnosticCardStudents(post, card.classroomId)
    return (
      <DiagnosticCard
        {...cardViewProps}
        backingPack={backingPack}
        backingUnitProgress={backingPack ? unitProgress[backingPack.unitId] : undefined}
        card={card}
        isBoosted={backingPack ? isUnitBoosted(backingPack.unitId) : false}
        key={diagnosticCardId(card.classroomId, card.pre.activity_id)}
        onPackChanged={handlePackChanged}
        onToggleBoost={backingPack ? boostToggler(backingPack.unitId) : undefined}
        onTogglePostBoost={postBackingPack ? boostToggler(postBackingPack.unitId) : undefined}
        postBackingPack={postBackingPack}
        postBackingUnitProgress={postBackingPack ? unitProgress[postBackingPack.unitId] : undefined}
        postIsBoosted={postBackingPack ? isUnitBoosted(postBackingPack.unitId) : false}
        postStudents={postStudents}
        preStudents={preStudents}
        recommendationCounts={recommendationCounts}
        selectedClassroomId={selectedClassroomId}
      />
    )
  }

  function renderPackCard(pack: Pack) {
    if (isProperDiagnosticUnit(pack) && !diagnosticRendersAsCustomPack(pack)) {
      const card = diagnosticCardForPack(pack)
      if (!card) { return null }
      if (packRendersJourneyCard(pack, card)) { return renderDiagnosticCard(card) }
      if (packIsCardAnchor(pack, card)) { return null }
    }
    return (
      <PackCard
        {...cardViewProps}
        isBoosted={isUnitBoosted(pack.unitId)}
        key={packCardId(pack.unitId)}
        onPackChanged={handlePackChanged}
        onToggleBoost={boostToggler(pack.unitId)}
        pack={pack}
        parentDiagnosticHref={parentDiagnosticHref(pack)}
        parentDiagnosticLabel={parentDiagnosticLabel(pack)}
        recommendationCounts={recommendationCounts}
        selectedClassroomId={selectedClassroomId}
        unitProgress={unitProgress[pack.unitId]}
      />
    )
  }

  function renderPackCards(packsToRender: Pack[], trailingCard?: React.ReactNode) {
    if (!packsToRender.length && !trailingCard) { return null }
    const renderedDiagnostics = new Set<string>()
    const cards = packsToRender.map(pack => {
      const blobKey = diagnosticBlobKey(pack)
      if (blobKey) {
        if (renderedDiagnostics.has(blobKey)) { return null }
        renderedDiagnostics.add(blobKey)
      }
      return renderPackCard(pack)
    }).filter(Boolean)
    if (!cards.length && !trailingCard) { return null }
    return <TruncatedCardGrid cards={cards} revealCardKey={revealCardKey} trailingCard={trailingCard} />
  }

  function handleGroupByChange(newGroupBy: string) {
    setRevealCardKey(rememberTopCard())
    window.localStorage.setItem(GROUP_BY_STORAGE_KEY, newGroupBy)
    setGroupBy(newGroupBy)
  }

  function handleShownPartsChange(parts: CardPart[]) {
    rememberTopCard()
    setShownParts(parts)
  }

  function handleDensityChange(newDensity: string) {
    rememberTopCard()
    window.localStorage.setItem(DENSITY_STORAGE_KEY, newDensity)
    setDensity(newDensity)
  }

  function showOpenView() { setView(OPEN) }
  function showClosedView() { setView(CLOSED) }
  function handleClassroomOption(option: { value: number }) { handleClassroomChange(option.value) }
  function showListDensity() { handleDensityChange(LIST_VIEW) }
  function showGridDensity() { handleDensityChange(GRID_VIEW) }

  const isLoading = !loaded || selectedClassroomId === null
  useScrollRestoredAfterLoad(SCROLL_POSITION_KEY, !isLoading)

  const isSearching = searchQuery !== ''
  const hasActiveFilters = selectedCompletion !== ALL || isSearching

  function cardsInSortOrder(diagnosticCardsToRender: DiagnosticCardData[], packsToRender: Pack[]): React.ReactNode[] {
    const entries: Array<{ card?: DiagnosticCardData, pack?: Pack }> = [
      ...diagnosticCardsToRender.map(card => ({ card })),
      ...packsToRender.map(pack => ({ pack })),
    ]
    return sortByTime(entries, entry => (entry.card ? diagnosticTime(entry.card) : packSortTime(entry.pack)))
      .map(entry => (entry.card ? renderDiagnosticCard(entry.card) : renderPackCard(entry.pack)))
      .filter(Boolean)
  }

  function buildSearchCards(): React.ReactNode[] {
    const renderedDiagnostics = new Set(sortedDiagnosticCards.map(card => `${card.classroomId}-${card.pre.activity_id}`))
    const searchPacks = sortPacks(filteredPacks).filter(pack => {
      const blobKey = diagnosticBlobKey(pack)
      if (!blobKey) { return true }
      if (renderedDiagnostics.has(blobKey)) { return false }
      renderedDiagnostics.add(blobKey)
      return true
    })
    if (groupBy === GROUP_BY_NONE) { return cardsInSortOrder(sortedDiagnosticCards, searchPacks) }
    return [...sortedDiagnosticCards.map(renderDiagnosticCard), ...searchPacks.map(renderPackCard)].filter(Boolean)
  }
  const searchCards = isSearching ? buildSearchCards() : []

  const isDiagnosticCardBoosted = (card: DiagnosticCardData) => {
    const backingPack = backingPackFor(card)
    if (backingPack && isUnitBoosted(backingPack.unitId)) { return true }
    const postBackingPack = postBackingPackFor(card)
    return Boolean(postBackingPack && isUnitBoosted(postBackingPack.unitId))
  }
  const [boostedDiagnosticCards, unboostedDiagnosticCards] = view === OPEN
    ? partition(sortedDiagnosticCards, isDiagnosticCardBoosted)
    : [[], sortedDiagnosticCards] as [DiagnosticCardData[], DiagnosticCardData[]]
  const [boostedRecommendationPacks, unboostedRecommendationPacks] = view === OPEN
    ? partition(recommendationPacks, pack => isUnitBoosted(pack.unitId))
    : [[], recommendationPacks] as [Pack[], Pack[]]
  const [boostedPremadePacks, unboostedPremadePacks] = view === OPEN
    ? partition(premadeAndCustomPacks, pack => isUnitBoosted(pack.unitId))
    : [[], premadeAndCustomPacks] as [Pack[], Pack[]]
  const boostedCount = boostedDiagnosticCards.length + boostedRecommendationPacks.length + boostedPremadePacks.length

  const diagnosticsCount = unboostedDiagnosticCards.length
  const anyResultsInView = boostedCount > 0 || diagnosticsCount > 0 || recommendationPacks.length > 0 || premadeAndCustomPacks.length > 0
  const noPacksAtAll = !anyResultsInView && !hasActiveFilters

  function renderUngroupedSection(id: string) {
    const cards = cardsInSortOrder(sortedDiagnosticCards, [...recommendationPacks, ...premadeAndCustomPacks])
    if (!cards.length) { return null }
    return (
      <section className="pack-section" id={id}>
        <h2 className="sr-only">Activity packs</h2>
        <div className="pack-card-grid">{cards}</div>
      </section>
    )
  }

  function resetFilters() {
    setSelectedCompletion(ALL)
    setSearchQuery('')
  }

  return (
    <main className={`manage-activities-v2 gray-background-accommodate-footer ${density === LIST_VIEW ? 'density-list' : ''}`}>
      <nav aria-label="Activity pack views" className="tab-subnavigation-wrapper desktop manage-activities-v2-subnav">
        <div className="container">
          <ul>
            <li>
              <button aria-current={view === OPEN ? 'true' : undefined} className={`focus-on-dark ${view === OPEN ? 'active' : ''}`} onClick={showOpenView} type="button">Open Activity Packs</button>
            </li>
            <li>
              <button aria-current={view === CLOSED ? 'true' : undefined} className={`focus-on-dark ${view === CLOSED ? 'active' : ''}`} onClick={showClosedView} type="button">Closed Activity Packs</button>
            </li>
            <li>
              <a className="focus-on-dark" href="/teachers/classrooms/activity_planner/lessons">Launch Lessons</a>
            </li>
          </ul>
        </div>
      </nav>

      {!isLoading && (
        <section className={`manage-activities-v2-header ${noPacksAtAll ? 'manage-activities-v2-header--divided' : ''}`}>
          <div className="container">
            <div className="title-row">
              <h1>{view === CLOSED ? 'Closed Activity Packs' : 'Open Activity Packs'}</h1>
              <div className="title-row-actions">
                <a className="quill-button outlined small grey focus-on-light" href="https://support.quill.org/en/articles/16964401-manage-activities-v2" rel="noopener noreferrer" target="_blank">
                  <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/file-document.svg`} />
                  <span>Guide</span>
                </a>
                <DropdownInput
                  ariaLabel="Class"
                  className="small bordered focus-on-light"
                  customMenuIconUrl={`${EXTRA_SMALL_ICON_BASE_SRC}/group.svg`}
                  disabled={classroomOptions.length === 0}
                  handleChange={handleClassroomOption}
                  isSearchable={true}
                  options={classroomOptions}
                  placeholder="No classes"
                  value={classroomOptions.find(option => option.value === selectedClassroomId) || classroomOptions[0] || null}
                />
                <a className="quill-button contained small green focus-on-light" href="/assign">Assign activities</a>
              </div>
            </div>
          </div>
        </section>
      )}

      {!isLoading && !noPacksAtAll && (
        <FilterRow
          groupBy={groupBy}
          isListView={density === LIST_VIEW}
          onCompletionChange={setSelectedCompletion}
          onGroupByChange={handleGroupByChange}
          onSearchQueryChange={setSearchQuery}
          onShowGrid={showGridDensity}
          onShowList={showListDensity}
          onShownPartsChange={handleShownPartsChange}
          onSortChange={setSortBy}
          searchQuery={searchQuery}
          selectedCompletion={selectedCompletion}
          shownParts={shownParts}
          sortBy={sortBy}
        />
      )}

      <Snackbar text={snackbarCopy} visible={showSnackbar} />

      {isLoading && <Spinner />}

      {!isLoading && view === CLOSED && (<div className="container sections-container">
        {isSearching ? (
          <section className="pack-section" id="closed-search-results">
            <div className="pack-section-header">
              <h2>Results for &ldquo;{searchQuery}&rdquo;</h2>
            </div>
            {searchCards.length
              ? <div className="pack-card-grid">{searchCards}</div>
              : <NoResultsState header="No activity packs found" onReset={resetFilters} showReset={true} />}
          </section>
        ) : (<React.Fragment>
          {!anyResultsInView && (
            <div className="pack-section">
              <NoResultsState
                header={hasActiveFilters ? 'No closed activity packs found' : "You haven't closed any activity packs"}
                onReset={resetFilters}
                plain={!hasActiveFilters}
                showReset={hasActiveFilters}
              />
            </div>
          )}

          {groupBy === GROUP_BY_NONE ? renderUngroupedSection('closed-activity-packs') : (<React.Fragment>
            {diagnosticsCount > 0 && (
              <Section icon={sectionIcon('search')} id="closed-diagnostics" title="Diagnostics">
                <TruncatedCardGrid cards={unboostedDiagnosticCards.map(renderDiagnosticCard)} itemLabel="diagnostics" revealCardKey={revealCardKey} />
              </Section>
            )}

            {unboostedRecommendationPacks.length > 0 && (
              <Section icon={sectionIcon('star')} id="closed-diagnostic-recommendations" title="Diagnostic Recommendations">
                {renderPackCards(sortPacks(unboostedRecommendationPacks))}
              </Section>
            )}

            {unboostedPremadePacks.length > 0 && (
              <Section icon={sectionIcon('folder')} id="closed-premade-and-custom" title="Pre-Made & Custom">
                {renderPackCards(sortPacks(unboostedPremadePacks))}
              </Section>
            )}
          </React.Fragment>)}
        </React.Fragment>)}
      </div>)}

      {!isLoading && view === OPEN && (<div className="container sections-container">
        {isSearching ? (
          <section className="pack-section" id="search-results">
            <div className="pack-section-header">
              <h2>Results for &ldquo;{searchQuery}&rdquo;</h2>
            </div>
            {searchCards.length
              ? <div className="pack-card-grid">{searchCards}</div>
              : <NoResultsState header="No activity packs found" onReset={resetFilters} showReset={true} />}
          </section>
        ) : (<React.Fragment>
          {!anyResultsInView && (
            hasActiveFilters ? (
              <div className="pack-section">
                <NoResultsState header="No activity packs match your filters" onReset={resetFilters} showReset={true} />
              </div>
            ) : (
              <div className="pack-section">
                <NoResultsState cta={{ text: 'Assign activities', href: '/assign' }} header="Start by assigning activities" plain={true} />
              </div>
            )
          )}

          {groupBy !== GROUP_BY_NONE && selectedClassroomId !== ALL && boostedCount > 0 && (
            <Section icon={sectionBoltIcon} id={BOOSTED_SECTION_ID} title="Boosted">
              <TruncatedCardGrid
                cards={[
                  ...boostedDiagnosticCards.map(renderDiagnosticCard),
                  ...sortPacks(boostedRecommendationPacks).map(renderPackCard),
                  ...sortPacks(boostedPremadePacks).map(renderPackCard),
                ]}
                itemLabel="boosted packs"
                revealCardKey={revealCardKey}
              />
            </Section>
          )}

          {groupBy === GROUP_BY_NONE ? renderUngroupedSection('activity-packs') : (<React.Fragment>
            {diagnosticsCount > 0 && (
              <Section icon={sectionIcon('search')} id="diagnostics" title="Diagnostics">
                <TruncatedCardGrid cards={unboostedDiagnosticCards.map(renderDiagnosticCard)} itemLabel="diagnostics" revealCardKey={revealCardKey} />
              </Section>
            )}

            {unboostedRecommendationPacks.length > 0 && (
              <Section icon={sectionIcon('star')} id="diagnostic-recommendations" title="Diagnostic Recommendations">
                {renderPackCards(sortPacks(unboostedRecommendationPacks))}
              </Section>
            )}

            {!noPacksAtAll && (unboostedPremadePacks.length > 0 || (premadeAndCustomPacks.length === 0 && !hasActiveFilters)) && (
              <Section icon={sectionIcon('folder')} id="premade-and-custom" title="Pre-Made & Custom">
                {renderPackCards(sortPacks(unboostedPremadePacks), (!hasActiveFilters && premadeAndCustomPacks.length === 0) ? createPackCard : undefined)}
              </Section>
            )}
          </React.Fragment>)}
        </React.Fragment>)}
      </div>)}
    </main>
  )
}
